// testHistoryApiService.js
// Service for fetching saved test results from the database

import axios from './authService';
import { API_ENDPOINTS } from '../config/apiConfig';

/**
 * Transform API response to match component expectations
 * @param {Object} apiResult - Raw result from API
 * @returns {Object} Transformed result
 */
const transformTestResult = (apiResult) => {
  // Transform validation_state structure
  // API returns validation_state as array directly: [...]
  // Components expect: { output: [...], metadata: {...} }
  let validationState = null;
  if (apiResult.validation_state) {
    if (Array.isArray(apiResult.validation_state)) {
      // Wrap array in expected structure
      validationState = {
        output: apiResult.validation_state,
        metadata: {
          flowId: apiResult.flow_id,
          flowName: apiResult.flow_name,
          testCaseName: apiResult.test_case_id,
          deviceId: apiResult.device_id_1,
          deviceName: apiResult.device_name,
          market: apiResult.market_name,
        },
      };
    } else {
      // Already in correct format
      validationState = apiResult.validation_state;
    }
  } else if (apiResult.redux_state) {
    validationState = apiResult.redux_state;
  }

  return {
    id: apiResult.id,
    regression_test_id: apiResult.regression_test_id,
    flow_name: apiResult.flow_name,
    flow_id: apiResult.flow_id,
    test_case_name: apiResult.test_case_id, // API uses test_case_id
    device_name: apiResult.device_name,
    device_id: apiResult.device_id_1, // Using device_id_1 as the primary device ID
    device_type: apiResult.device_type,
    market: apiResult.market_name, // API uses market_name
    user_email: apiResult.tester_email, // API uses tester_email
    created_at: apiResult.executed_at, // API uses executed_at
    timestamp_start: new Date(apiResult.start_time).getTime(), // Convert ISO string to milliseconds
    timestamp_end: new Date(apiResult.end_time).getTime(), // Convert ISO string to milliseconds
    total_events: apiResult.summary?.total_events || 0,
    passed_events: apiResult.summary?.passed || 0,
    failed_events: apiResult.summary?.failed || 0,
    missing_events: apiResult.summary?.missing || 0,
    non_validated_events: apiResult.summary?.non_validated || 0,
    not_present_in_app: apiResult.summary?.not_present_in_app || 0,
    order_issues: apiResult.summary?.order_issues || 0,
    pass_rate: parseFloat(apiResult.summary?.pass_rate) || 0, // Convert string to number
    notes: apiResult.notes || '', // Add notes if available
    validation_state: validationState,
  };
};

/**
 * Search for test results based on filters
 * @param {Object} filters - Search filters
 * @param {string} filters.regression_test_id - Regression test ID (optional)
 * @param {number} filters.market_id - Market ID (optional)
 * @param {string} filters.tester_email - User email filter (optional)
 * @param {number} filters.device_id - Device database ID (optional)
 * @returns {Promise<Array>} Array of test result summary objects
 */
export const searchTestResults = async (filters) => {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    if (filters.regression_test_id) params.append('regression_test_id', filters.regression_test_id);
    if (filters.market_id) params.append('market_id', filters.market_id);
    if (filters.tester_email) params.append('tester_email', filters.tester_email);
    if (filters.device_id) params.append('device_id', filters.device_id);

    const url = `${API_ENDPOINTS.GET_REGRESSION_TEST_RESULT}?${params.toString()}`;

    const response = await axios.get(url);
    const data = response.data;

    // Extract the array from response
    const results = Array.isArray(data) ? data : data.data || [];

    // Transform each result to match component expectations
    const transformedResults = results.map(transformTestResult);

    return transformedResults;
  } catch (error) {
    throw new Error(`Failed to search test results: ${error.message}`);
  }
};

/**
 * Get detailed test result including validation state
 * @param {number} testResultId - The ID of the test result
 * @returns {Promise<Object>} Test result object with validation_state
 */
export const getTestResultDetail = async (testResultId) => {
  try {
    const url = `${API_ENDPOINTS.GET_REGRESSION_TEST_RESULT}?id=${testResultId}`;

    const response = await axios.get(url);
    const data = response.data;

    // Extract the result (adjust based on actual API response structure)
    let result = data.data || data;

    // Detail view returns array with single object, extract first element
    if (Array.isArray(result) && result.length > 0) {
      result = result[0];
    }

    // Transform the result to match component expectations
    const transformedResult = transformTestResult(result);

    return transformedResult;
  } catch (error) {
    throw new Error(`Failed to fetch test result detail: ${error.message}`);
  }
};

export const getConfluenceUrl = async (jobId) => {
  const url = `${API_ENDPOINTS.GET_CONFLUENCE_URL}?job_id=${jobId}`;

  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Confluence URL', error);
    throw error;
  }
};

/**
 * Export test results to Confluence
 * @param {Object} params - Export parameters
 * @param {string} params.regression_test_id - Required regression test ID
 * @param {number} params.device_id - Optional device ID
 * @param {number} params.market_id - Optional market ID
 * @param {string} params.tester_email - Optional tester email
 * @returns {Promise<Object>} { message: string, link: string }
 */
export const exportToConfluence = async (params) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params.regression_test_id)
      queryParams.append('regression_test_id', params.regression_test_id);
    if (params.device_id) queryParams.append('device_id', params.device_id);
    if (params.market_id) queryParams.append('market_id', params.market_id);
    if (params.tester_email) queryParams.append('tester_email', params.tester_email);

    const url = `${API_ENDPOINTS.EXPORT_TO_CONFLUENCE}?${queryParams.toString()}`;

    const response = await axios.post(url);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to export to Confluence: ${error.message}`);
  }
};

/**
 * Update notes for a regression test result
 * @param {number} id - Test result ID
 * @param {string} notes - Updated notes
 */
export const updateTestResultNotes = async (id, notes) => {
  try {
    const url = `${API_ENDPOINTS.UPDATE_REGRESSION_TEST_RESULT_DATA}?id=${id}`;

    const response = await axios.put(url, { notes });

    return response.data;
  } catch (error) {
    throw new Error(`Failed to update notes: ${error.message}`);
  }
};

export const updateValidationState = async (id, validation_state, summary) => {
  try {
    const url = `${API_ENDPOINTS.UPDATE_REGRESSION_TEST_RESULT_DATA}?id=${id}`;

    const response = await axios.put(url, {
      validation_state: JSON.stringify(validation_state),
      summary: JSON.stringify(summary),
    });

    return response.data;
  } catch (error) {
    throw new Error(`Failed to update validation state: ${error.message}`);
  }
};
